// ChatContainer.js
import React, { PureComponent, createRef } from "react";
import { connect } from "react-redux";
import { Send, Mic, Loader2, Users, Bot, ArrowRight } from "lucide-react";
import { Button, CardBody, Spinner, Modal, ModalBody } from "reactstrap";
import BotMessage from "./BotMessage";
import UserMessage from "./UserMessage";
import { cn } from "../../../utils/utils";
import PatientAppServices from "../../../services/PatientAppServices";
import CitationInfoPopup from "./CitationInfoPopup";
import CanvasMessage from "./CanvasMessage";
import CanvasEditModal from "./CanvasEditModal";
import MPSButton from "../../../components/Buttons/Buttons/MPSButton";
import Text from "../../../components/Text";
import Messages from "../../../Shared/Messages";
import I18nProvider from "../../../services/I18nProvider";
import AgentCard from "./AgentCard";
import AgentSelector from "./AgentSelector";
import aiAgentsService from "../../../services/aiAgentService";

import "./styles.scss";

class ChatContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      sessionId: null,
      messages: [],
      input: "",
      isLoading: false,
      isRecording: false,
      isTranscribing: false,
      loadingText: "Thinking…",
      isCitationPopupOpen: false,
      selectedCitationTitle: "",
      autoPlayAudio: false,
      canvasEditModalOpen: false,
      canvasToEdit: null,
      activeCanvasMessages: {},
      lastSavedIndex: -1,
      isLoadingHistory: false,
      availableAgents: [],
      selectedAgent: null,
      showAgentSelector: false,
      isLoadingAgents: false,
    };
    let external = window.location.pathname.includes("/e/chat");
    this.initialPrompts = !external ? props.chatUiConfig?.initialPrompts || [] : [];
    this.avatarSrc = !external ? props.chatUiConfig?.avatarImage : null;
    this.state = {
      ...this.state,
      showInitialPrompts: true,
    };
    this.audioContextRef = createRef(null);
    this.mediaStreamRef = createRef(null);
    this.processorRef = createRef(null);
    this.audioDataRef = createRef([]);
    this.sampleRateRef = createRef(null);

    this.messagesBoxRef = createRef();

    this.loadingMessages = [
      "Thinking…",
      "Gathering sources…",
      "Searching knowledge base…",
      "Finding answers…",
      "Verifying data…",
    ];
    this.loadingIndex = 0;
    this.loadingInterval = null;
  }

  componentDidMount() {
    // Load existing chat session
    this.loadSession();
    
    // Load available agents
    this.loadAgents();

    // Add listener for browser navigation
    window.addEventListener("beforeunload", this.saveNewMessages);

    const voiceSupported =
      "webkitSpeechRecognition" in window || "SpeechRecognition" in window;
    this.setState({ voiceSupported });

    if (voiceSupported) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = false;
      this.recognition.lang = "en-US";

      // Add these properties to help with network issues
      this.recognition.maxAlternatives = 1;
      this.recognition.serviceURI = null; // Use default service

      this.recognition.onstart = () => {
        console.log("Voice recognition started");
        this.setState({ isListening: true });
      };

      this.recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        console.log("Voice transcript received:", transcript);
        this.setState({
          input: transcript,
          isListening: false,
        });
      };

      this.recognition.onerror = (event) => {
        console.error("Voice recognition error:", event.error);
        this.setState({ isListening: false });

        // Handle different error types
        switch (event.error) {
          case "network":
            alert(
              "Network error. Please check your internet connection and try again."
            );
            break;
          case "not-allowed":
            alert(
              "Microphone access denied. Please allow microphone access and try again."
            );
            break;
          case "service-not-allowed":
            alert("Speech recognition service not allowed. Please try again.");
            break;
          case "bad-grammar":
            alert("Speech recognition grammar error. Please try again.");
            break;
          case "language-not-supported":
            alert("Language not supported. Switching to default language.");
            this.recognition.lang = "en-US";
            break;
          case "no-speech":
            alert("No speech detected. Please try speaking again.");
            break;
          case "audio-capture":
            alert(
              "Audio capture error. Please check your microphone and try again."
            );
            break;
          default:
            if (event.error !== "aborted") {
              alert(
                `Voice recognition error: ${event.error}. Please try again.`
              );
            }
        }
      };

      this.recognition.onend = () => {
        console.log("Voice recognition ended");
        this.setState({ isListening: false });
      };
    }
  }

  componentWillUnmount() {
    // Save any unsaved messages before unmounting
    this.saveNewMessages();

    // Remove event listener
    window.removeEventListener("beforeunload", this.saveNewMessages);

    this.cleanupAudioRecording();
    if (this.recognition) {
      this.recognition.abort();
    }
  }

  componentDidUpdate(_, prevState) {
    if (prevState.isLoading !== this.state.isLoading) {
      if (this.state.isLoading) {
        this.loadingInterval = setInterval(() => {
          this.loadingIndex =
            (this.loadingIndex + 1) % this.loadingMessages.length;
          this.setState({
            loadingText: this.loadingMessages[this.loadingIndex],
          });
        }, 2500);
      } else {
        clearInterval(this.loadingInterval);
        this.loadingInterval = null;
        this.loadingIndex = 0;
        this.setState({ loadingText: this.loadingMessages[0] });
      }
    }

    // Auto-scroll when messages change
    if (prevState.messages.length !== this.state.messages.length) {
      if (this.props.onChatUpdate) {
        this.props.onChatUpdate(this.state.messages);
      }

      // Auto-scroll to bottom with slight delay to ensure DOM is updated
      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    }

    // Resize textareas whenever input changes
    if (prevState.input !== this.state.input) {
      setTimeout(() => {
        this.resizeTextarea();
      }, 0);
    }
  }

  // Helper function to map the new API history to our component's message format
  mapApiHistoryToMessages = (apiHistory) => {
    if (!Array.isArray(apiHistory)) return [];

    return apiHistory
      .filter(
        (msg) => msg.type === "human" || (msg.type === "ai" && msg.data.content)
      ) // Filter out tool calls or AI messages with no content
      .map((apiMsg) => ({
        id: apiMsg.data.id || Date.now() + Math.random(), // Use the ID from the API
        content: apiMsg.data.content,
        role: apiMsg.type === "human" ? "User" : "Assistant",
        timestamp: new Date(),
        textStreamComplete: true, // Always true in non-streaming mode
        audioData: null,
        citations: apiMsg.data.citations || null,
        urls: apiMsg.data.urls || null,
      }));
  };

  setData = (data) => {
    this.setState({
      input: data.valueInteger.toString() || this.state.input.toString(),
    });
  };
  getValue = (question) => {
    let { answers = [] } = question;
    if (answers.length >= 1) {
      return answers[0].valueInteger;
    }

    if (this.state.input) {
      return this.state.input;
    }

    if (question.initialValue?.valueInteger !== undefined) {
      return question.initialValue.valueInteger.toString();
    }

    return "0";
  };

  // Safely escape plain text into HTML
  escapeHtml = (s = "") =>
    String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  // Convert text (with \n) into HTML. Uses paragraphs and <br/> for single newlines.
  formatStreamTextToHtml = (text) => {
    const normalized = String(text || "").replace(/\r\n/g, "\n");
    const blocks = normalized.split(/\n{2,}/); // paragraph breaks
    const html = blocks
      .map((b) => `<p>${this.escapeHtml(b).replace(/\n/g, "<br/>")}</p>`)
      .join("");
    // Optional: if you have a sanitizer that preserves basic styles, call it here
    // try { return sanitizeHTML ? sanitizeHTML(html) : html; } catch { return html; }
    return html;
  };

  // Load available agents
  loadAgents = async () => {
    const { patientTypeId, assistantId, agentMode } = this.props;
    
    this.setState({ isLoadingAgents: true });
    
    try {
      let agents = [];
      
      if (agentMode) {
        // In agent mode, only load react agents
        const reactAgents = await aiAgentsService.getReactAgents(patientTypeId).catch(() => []);
        agents = (reactAgents || []).map(a => ({
          ...a,
          type: 'react-agent',
          description: a.description || a.systemPrompt?.substring(0, 100) || 'Agent',
          capabilities: a.tools || []
        }));
      } else {
        // In chat mode, only load assistants
        const assistants = await aiAgentsService.getAssistants(patientTypeId).catch(() => []);
        agents = (assistants || []).map(a => ({
          ...a,
          type: 'assistant',
          description: a.chatUiConfig?.description || a.systemPrompt?.substring(0, 100) || 'AI Assistant',
          capabilities: a.chatUiConfig?.capabilities || []
        }));
      }
      
      // If an assistantId is provided, select that agent
      let selectedAgent = null;
      if (assistantId) {
        selectedAgent = agents.find(a => a.id === assistantId);
      }
      
      this.setState({ 
        availableAgents: agents,
        selectedAgent,
        isLoadingAgents: false
      }, () => {
        // Notify parent about initial agent selection
        if (selectedAgent && this.props.onAgentChange) {
          this.props.onAgentChange(selectedAgent);
        }
      });
    } catch (error) {
      console.error('Failed to load agents:', error);
      this.setState({ isLoadingAgents: false });
    }
  };
  
  // Load existing chat session
  loadSession = async () => {
    const { assistantId, contextPatientId } = this.props;
    const { selectedAgent } = this.state;

    // Use selected agent ID if available, otherwise use prop
    const agentId = selectedAgent?.id || assistantId;
    if (!agentId) return;

    // Show loading indicator
    this.setState({ isLoadingHistory: true });

    try {
      const existingMessages = await PatientAppServices.loadChatMessages({
        assistantId: agentId,
        contextPatientId,
        limit: 100,
      });

      if (existingMessages && existingMessages.length > 0) {
        // Map backend messages to component format
        const mappedMessages = existingMessages.map((msg) => ({
          id: msg._id || msg.id || Date.now().toString(),
          content: msg.content,
          role:
            msg.role === "user"
              ? "User"
              : msg.role === "canvas"
              ? "Canvas"
              : "Assistant",
          timestamp: new Date(msg.timestamp),
          citations: msg.citations || [],
          urls: msg.urls || [],
          audioData: msg.audioData || null,
          textStreamComplete: true,
          isStreaming: false,
          isHistorical: true, // Mark as historical to prevent autoplay
          // Restore canvas-specific properties (only for Canvas role)
          ...(msg.canvasTitle && { canvasTitle: msg.canvasTitle }),
          ...(msg.surveyData && { surveyData: msg.surveyData }),
          ...(msg.attributeData && { attributeData: msg.attributeData }),
        }));

        // Build activeCanvasMessages for canvas messages
        const activeCanvasMessages = {};
        mappedMessages.forEach((msg) => {
          if (msg.role === "Canvas") {
            activeCanvasMessages[msg.id] = msg;
          }
        });

        this.setState({
          sessionId: existingMessages[0].sessionId,
          messages: mappedMessages,
          lastSavedIndex: mappedMessages.length - 1,
          // showInitialPrompts: false, // Hide prompts if we have messages
          isLoadingHistory: false,
          activeCanvasMessages,
        });
      } else {
        // No messages found, just hide the loader
        this.setState({ isLoadingHistory: false });
      }
    } catch (error) {
      console.error("Failed to load chat session:", error);
      // Hide loader on error
      this.setState({ isLoadingHistory: false });
    }
  };

  // Save only new messages
  saveNewMessages = async () => {
    const { messages, lastSavedIndex, sessionId } = this.state;

    // Get only unsaved messages
    const newMessages = messages.slice(lastSavedIndex + 1);

    if (newMessages.length === 0 || !sessionId) return;

    const messagesToSave = newMessages.map((msg) => ({
      sessionId: sessionId,
      role: msg.role.toLowerCase(),
      content: msg.content,
      timestamp: msg.timestamp,
      ...(msg.citations &&
        msg.citations.length > 0 && { citations: msg.citations }),
      ...(msg.urls && msg.urls.length > 0 && { urls: msg.urls }),
      ...(msg.audioData && { audioData: msg.audioData }),
      // Add canvas-specific properties (only for Canvas role)
      ...(msg.canvasTitle && { canvasTitle: msg.canvasTitle }),
      ...(msg.surveyData && { surveyData: msg.surveyData }),
      ...(msg.attributeData && { attributeData: msg.attributeData }),
    }));

    try {
      await PatientAppServices.saveNewChatMessages(messagesToSave);

      // Update state to reflect saved messages
      this.setState({
        lastSavedIndex: messages.length - 1,
      });
    } catch (error) {
      console.error("Failed to save messages:", error);
    }
  };

  scrollToBottom = () => {
    if (!this.messagesBoxRef.current) return;

    const messagesContainer = this.messagesBoxRef.current;
    const lastMessage = this.state.messages[this.state.messages.length - 1];

    // Check if there's an assessment field present
    const assessmentField = messagesContainer.querySelector(
      ".chat-assessment-field-inline"
    );

    if (
      assessmentField &&
      lastMessage?.asking_question &&
      lastMessage?.current_question_info
    ) {
      // If assessment field exists, scroll to show it
      assessmentField.scrollIntoView({
        behavior: "smooth",
        block: "end", // Show the assessment at the bottom of the viewport
        inline: "nearest",
      });
    } else {
      // Otherwise, scroll to the very bottom
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // Optional: throttle streaming UI updates
  _raf = null;
  _schedule = (fn) => {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(fn);
  };

  // --- REWRITTEN: handleSendMessage to format streaming text as HTML ---
  handleSendMessage = async () => {
    const { input, sessionId, selectedAgent } = this.state;
    const prompt = input?.trim();
    if (!prompt) {
      return;
    }
    const payload = { message: prompt };
    if (sessionId) {
      payload.sessionId = sessionId;
    }
    // Use selected agent or fallback to prop
    const agentId = selectedAgent?.id || this.props.assistantId;
    if (agentId) {
      // Send agentId for react-agents, assistantId for assistants
      if (selectedAgent?.type === 'react-agent') {
        payload.agentId = agentId;
      } else {
        payload.assistantId = agentId;
      }
    }
    if (this.props.contextPatientId) {
      payload.contextPatientId = this.props.contextPatientId;
    }

    const userMessage = {
      id: Date.now().toString(),
      content: prompt,
      role: "User",
      timestamp: new Date(),
    };
    const assistantMessageId = (Date.now() + 1).toString();

    this.setState((prevState) => ({
      messages: [...prevState.messages, userMessage],
      input: "",
      isLoading: true,
      streamingMessageId: assistantMessageId,
    }));

    // Accumulate raw text, format to HTML for UI
    let accumulatedText = "";
    let accumulatedHtml = "";
    let toolsInProgress = [];
    let isReceivingTokens = false;
    let assistantMessageAdded = false;

    let canvasMessageId = null;

    try {
      await PatientAppServices.streamChat(payload, {
        onThinking: () => {
          if (!isReceivingTokens && toolsInProgress.length === 0) {
            this.setState({ loadingText: "Thinking..." });
          }
        },

        onToken: (token) => {
          accumulatedText += token; // raw text
          accumulatedHtml = this.formatStreamTextToHtml(accumulatedText); // formatted HTML
          isReceivingTokens = true;

          if (!assistantMessageAdded) {
            const assistantMessage = {
              id: assistantMessageId,
              content: accumulatedHtml, // HTML
              role: "Assistant",
              timestamp: new Date(),
              textStreamComplete: false,
              isStreaming: true,
              toolsInProgress: [],
              citations: [],
              urls: [],
              audioData: null,
              audioMimeType: "audio/mp3",
            };

            this.setState((prevState) => ({
              messages: [...prevState.messages, assistantMessage],
              isLoading: false,
              loadingText: "",
            }));
            assistantMessageAdded = true;
          } else {
            // Throttle updates to avoid excessive re-renders
            this._schedule(() => {
              this.setState((prevState) => ({
                messages: prevState.messages.map((m) =>
                  m.id === assistantMessageId
                    ? { ...m, content: accumulatedHtml }
                    : m
                ),
                isLoading: false,
                loadingText: "",
              }));
            });
          }
        },

        onToolStart: (toolName) => {
          toolsInProgress.push(toolName);
          this.setState((prevState) => ({
            loadingText: `Using: ${toolName}...`,
            messages: prevState.messages.map((m) =>
              m.id === assistantMessageId
                ? { ...m, toolsInProgress: [...toolsInProgress] }
                : m
            ),
          }));
        },

        onToolEnd: (toolName) => {
          toolsInProgress = toolsInProgress.filter((t) => t !== toolName);
          this.setState((prevState) => ({
            messages: prevState.messages.map((m) =>
              m.id === assistantMessageId
                ? { ...m, toolsInProgress: [...toolsInProgress] }
                : m
            ),
            loadingText:
              toolsInProgress.length > 0
                ? `Using: ${toolsInProgress.join(", ")}...`
                : "Processing...",
          }));
        },

        onCanvas: (canvasData) => {
          // Handle canvas/document events
          if (canvasData.action === "open") {
            // Create a new canvas message
            canvasMessageId = canvasData.id || `canvas-${Date.now()}`;
            const canvasMessage = {
              id: canvasMessageId,
              content: canvasData.content || "",
              role: "Canvas",
              timestamp: new Date(),
              canvasTitle: canvasData.title,
              canvasLoading: canvasData.loading,
              surveyData: canvasData.surveyData,
              attributeData: canvasData.attributeData,
              textStreamComplete: !canvasData.loading,
            };

            this.setState((prevState) => ({
              messages: [...prevState.messages, canvasMessage],
              activeCanvasMessages: {
                ...prevState.activeCanvasMessages,
                [canvasMessageId]: canvasMessage,
              },
              isLoading: false,
            }));
          } else if (canvasData.action === "update" && canvasMessageId) {
            // Update existing canvas message
            this.setState((prevState) => ({
              messages: prevState.messages.map((m) =>
                m.id === canvasMessageId
                  ? {
                      ...m,
                      content: canvasData.content,
                      canvasTitle: canvasData.title || m.canvasTitle, // Update title if provided
                      canvasLoading: canvasData.loading,
                      surveyData: canvasData.surveyData || m.surveyData,
                      attributeData:
                        canvasData.attributeData || m.attributeData,
                      textStreamComplete: !canvasData.loading,
                    }
                  : m
              ),
              activeCanvasMessages: {
                ...prevState.activeCanvasMessages,
                [canvasMessageId]: {
                  ...prevState.activeCanvasMessages[canvasMessageId],
                  content: canvasData.content,
                  canvasTitle:
                    canvasData.title ||
                    prevState.activeCanvasMessages[canvasMessageId]
                      ?.canvasTitle,
                  canvasLoading: canvasData.loading,
                  surveyData:
                    canvasData.surveyData ||
                    prevState.activeCanvasMessages[canvasMessageId]?.surveyData,
                  attributeData:
                    canvasData.attributeData ||
                    prevState.activeCanvasMessages[canvasMessageId]
                      ?.attributeData,
                },
              },
            }));
          }
        },

        onAudio: (audioData) => {
          // Audio always arrives after onFinal, so just update the existing message
          this.setState((prevState) => ({
            messages: prevState.messages.map((m) =>
              m.id === assistantMessageId
                ? { ...m, audioData, audioMimeType: "audio/mp3" }
                : m
            ),
          }));
        },

        // Prefer response.reply (final text); fall back to accumulatedText

        onFinal: (response) => {
          // Final response with complete data
          const finalText =
            typeof response.reply === "string" && response.reply.length > 0
              ? response.reply
              : accumulatedText;
          const finalHtml = this.formatStreamTextToHtml(finalText);
          // If no assistant message was added yet (no tokens were streamed), add it now
          if (!assistantMessageAdded) {
            const assistantMessage = {
              id: assistantMessageId,
              content: finalHtml, // HTML
              role: "Assistant",
              timestamp: new Date(),
              textStreamComplete: true,
              isStreaming: false,
              citations: response.citations || [],
              urls: response.urls || [],
              asking_question: response.asking_question || false,
              current_question_info: response.current_question_info || null,
              audioData: null, // Audio will be added later by onAudio
              audioMimeType: null,
              toolsInProgress: [],
            };
            setTimeout(() => {
              if (response.asking_question && response.current_question_info) {
                this.scrollToBottom();
              }
            }, 100);
            this.setState((prevState) => ({
              messages: [...prevState.messages, assistantMessage],
              sessionId: response.session_id || prevState.sessionId,
              isLoading: false,
              streamingMessageId: null,
              loadingText: "Thinking…",
            }));
          } else {
            this.setState((prevState) => ({
              messages: prevState.messages.map((m) =>
                m.id === assistantMessageId
                  ? {
                      ...m,
                      content: finalHtml, // HTML
                      textStreamComplete: true,
                      isStreaming: false,
                      citations: response.citations || [],
                      urls: response.urls || [],
                      asking_question: response.asking_question || false,
                      current_question_info:
                        response.current_question_info || null,
                      audioData: m.audioData, // Preserve existing audio if already set
                      audioMimeType: m.audioMimeType,
                      toolsInProgress: [],
                    }
                  : m
              ),
              sessionId: response.session_id || prevState.sessionId,
              isLoading: false,
              streamingMessageId: null,
              loadingText: "Thinking…",
            }));
          }
        },

        onClose: (message) => {
          // Stream completed
          console.log("Stream closed:", message);

          // Only update message if it was added
          if (assistantMessageAdded) {
            this.setState((prevState) => ({
              messages: prevState.messages.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      textStreamComplete: true,
                      isStreaming: false,
                      toolsInProgress: [],
                    }
                  : msg
              ),
              isLoading: false,
              streamingMessageId: null,
              loadingText: "Thinking…", // Reset loading text
            }));
          } else {
            // Just clear loading state if no message was added
            this.setState({
              isLoading: false,
              streamingMessageId: null,
              loadingText: "Thinking…",
            });
          }
        },

        onError: (error) => {
          console.error("Streaming error:", error);

          // Handle error - add or update message as appropriate
          if (!assistantMessageAdded) {
            // Add error message if no assistant message was created
            const errorMessage = {
              id: assistantMessageId,
              content: "I'm sorry, I encountered an error. Please try again.",
              role: "Assistant",
              timestamp: new Date(),
              textStreamComplete: true,
              isStreaming: false,
              toolsInProgress: [],
            };

            this.setState((prevState) => ({
              messages: [...prevState.messages, errorMessage],
              isLoading: false,
              streamingMessageId: null,
              loadingText: "Thinking…",
            }));
          } else {
            // Update existing message to show error
            this.setState((prevState) => ({
              messages: prevState.messages.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      content:
                        accumulatedHtml ||
                        "I'm sorry, I encountered an error. Please try again.",
                      textStreamComplete: true,
                      isStreaming: false,
                      toolsInProgress: [],
                    }
                  : msg
              ),
              isLoading: false,
              streamingMessageId: null,
              loadingText: "Thinking…", // Reset loading text
            }));
          }
        },

        onHttpStreamComplete: () => {
          // HTTP stream ended - ensure we clean up
          if (assistantMessageAdded) {
            this.setState((prevState) => ({
              messages: prevState.messages.map((msg) =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      textStreamComplete: true,
                      isStreaming: false,
                    }
                  : msg
              ),
              isLoading: false,
              streamingMessageId: null,
            }));
          } else {
            // Just clear loading state
            this.setState({
              isLoading: false,
              streamingMessageId: null,
            });
          }
        },
      });
    } catch (error) {
      console.error("Chat streaming error:", error);
      const errorMessage = {
        id: (Date.now() + 2).toString(),
        content: "I'm sorry, I encountered an error. Please try again.",
        role: "Assistant",
        timestamp: new Date(),
        textStreamComplete: true,
      };

      // Remove the incomplete streaming message and add error message
      this.setState((prevState) => ({
        messages: prevState.messages
          .filter((msg) => msg.id !== assistantMessageId)
          .concat(errorMessage),
        isLoading: false,
        streamingMessageId: null,
        loadingText: "Thinking…", // Reset loading text
      }));
    }
  };

  handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.handleSendMessage();
    }
  };

  handleOpenCitationPopup = (data) => {
    if (typeof data === "string") {
      this.setState({
        selectedCitationTitle: data, // For CitationInfoPopup to fetch
        selectedCitationArticle: null, // No pre-fetched article
        isCitationPopupOpen: true,
      });
    } else if (typeof data === "object" && data !== null) {
      this.setState({
        selectedCitationTitle: data.title || "Article", // Fallback title for header
        selectedCitationArticle: data, // Pre-fetched article
        isCitationPopupOpen: true,
      });
    }
  };

  toggleCitationPopup = () => {
    this.setState((prevState) => ({
      isCitationPopupOpen: !prevState.isCitationPopupOpen,
    }));
  };

  encodeWAV = (samples, sampleRate) => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    this.writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + samples.length * 2, true);
    this.writeString(view, 8, "WAVE");
    this.writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    this.writeString(view, 36, "data");
    view.setUint32(40, samples.length * 2, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return new Blob([view], { type: "audio/wav" });
  };

  writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  handleToggleRecording = async () => {
    // Use audio recording approach since Web Speech API is having network issues
    if (this.state.isRecording) {
      this.stopAudioContextRecording();
    } else {
      await this.startAudioContextRecording();
    }
  };

  startAudioContextRecording = async () => {
    console.log("Voice search button clicked!");

    if (this.state.isRecording) {
      console.log("Stopping audio recording...");
      this.stopAudioContextRecording();
      return;
    }

    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      alert("Audio recording is not supported by your browser.");
      return;
    }

    try {
      console.log("Requesting microphone access for recording...");

      // Add constraints that work better with Bluetooth devices
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100, // Explicit sample rate
          channelCount: 1, // Mono recording
          // Add latency hints for Bluetooth
          latency: 0.1,
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.mediaStreamRef.current = stream;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext({
        sampleRate: 44100, // Explicit sample rate for consistency
        latencyHint: "interactive", // Better for real-time recording
      });

      this.audioContextRef.current = audioContext;
      this.sampleRateRef.current = audioContext.sampleRate;

      const source = audioContext.createMediaStreamSource(stream);

      // Use a smaller buffer size for better Bluetooth compatibility
      const processor = audioContext.createScriptProcessor(2048, 1, 1);
      this.processorRef.current = processor;
      this.audioDataRef.current = [];

      processor.onaudioprocess = (event) => {
        const channelData = event.inputBuffer.getChannelData(0);
        this.audioDataRef.current.push(new Float32Array(channelData));
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      console.log("Audio recording started with Bluetooth-optimized settings");
      this.setState({ isRecording: true, input: "" });
    } catch (err) {
      console.error("Error accessing microphone:", err);
      let errorMessage = `Could not access microphone: ${err.message}`;

      if (err.name === "NotAllowedError") {
        errorMessage =
          "Microphone access denied. Please allow microphone access and try again.";
      } else if (err.name === "NotFoundError") {
        errorMessage =
          "No microphone found. If using Bluetooth headphones, make sure they're properly connected.";
      } else if (err.name === "OverconstrainedError") {
        errorMessage =
          "Audio constraints could not be satisfied. Try disconnecting Bluetooth devices and using built-in microphone.";
      }

      alert(errorMessage);
      this.setState({ isRecording: false });
    }
  };

  stopAudioContextRecording = () => {
    if (!this.state.isRecording || !this.processorRef.current) {
      return;
    }
    this.setState({ isRecording: false });

    this.processorRef.current.disconnect();
    this.processorRef.current.onaudioprocess = null;
    if (this.mediaStreamRef.current) {
      this.mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (
      this.audioContextRef.current &&
      this.audioContextRef.current.state !== "closed"
    ) {
      this.audioContextRef.current
        .close()
        .catch((e) => console.warn("Error closing AudioContext:", e));
    }

    const recordedPCMChunks = this.audioDataRef.current;
    if (recordedPCMChunks.length === 0) {
      return;
    }

    const totalLength = recordedPCMChunks.reduce(
      (sum, arr) => sum + arr.length,
      0
    );
    const mergedPCMBuffer = new Float32Array(totalLength);
    let offset = 0;
    recordedPCMChunks.forEach((buffer) => {
      mergedPCMBuffer.set(buffer, offset);
      offset += buffer.length;
    });

    const sampleRate = this.sampleRateRef.current || 44100;
    const wavBlob = this.encodeWAV(mergedPCMBuffer, sampleRate);

    if (wavBlob.size > 44) {
      this.getTranscriptionForInput(wavBlob);
    }
  };

  cleanupAudioRecording = () => {
    if (this.state.isRecording) {
      this.stopAudioContextRecording();
    } else {
      if (this.mediaStreamRef.current) {
        this.mediaStreamRef.current
          .getTracks()
          .forEach((track) => track.stop());
        this.mediaStreamRef.current = null;
      }
      if (this.processorRef.current) {
        try {
          this.processorRef.current.disconnect();
        } catch (e) {}
        this.processorRef.current = null;
      }
      if (
        this.audioContextRef.current &&
        this.audioContextRef.current.state !== "closed"
      ) {
        this.audioContextRef.current
          .close()
          .catch((e) =>
            console.warn("Error closing AudioContext on cleanup:", e)
          );
        this.audioContextRef.current = null;
      }
    }
    this.audioDataRef.current = [];
  };

  getTranscriptionForInput = async (audioBlob) => {
    if (!audioBlob) {
      return;
    }
    this.setState({ isTranscribing: true });
    const formData = new FormData();
    formData.append("audioFile", audioBlob, "user_audio.wav");

    try {
      const response = await PatientAppServices.getTranscription(formData);
      let transcriptionText = "";
      if (typeof response === "string") {
        transcriptionText = response;
      } else if (response && typeof response.prompt === "string") {
        transcriptionText = response.prompt;
      } else if (response && typeof response.transcription === "string") {
        transcriptionText = response.transcription;
      } else {
        console.error("Unexpected transcription response format:", response);
        alert("Failed to parse transcription from server response.");
        this.setState({ isTranscribing: false });
        return;
      }
      this.setState({
        input: transcriptionText,
        isTranscribing: false,
      });
    } catch (error) {
      console.error("Error getting transcription:", error);
      let errorMessage = "Error during transcription.";
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      this.setState({ isTranscribing: false });
      alert(errorMessage);
    }
  };

  // Add this method to toggle autoplay
  toggleAutoPlay = () => {
    this.setState((prevState) => ({
      autoPlayAudio: !prevState.autoPlayAudio,
    }));
  };

  // Canvas editing methods
  handleOpenCanvasEditor = (canvasMessage) => {
    this.setState({
      canvasEditModalOpen: true,
      canvasToEdit: canvasMessage,
    });
  };

  handleCloseCanvasEditor = () => {
    this.setState({
      canvasEditModalOpen: false,
      canvasToEdit: null,
    });
  };

  handleSaveCanvas = (updatedContent) => {
    const { canvasToEdit } = this.state;

    if (canvasToEdit) {
      // Update the canvas message with new content
      this.setState((prevState) => ({
        messages: prevState.messages.map((msg) =>
          msg.id === canvasToEdit.id ? { ...msg, content: updatedContent } : msg
        ),
        activeCanvasMessages: {
          ...prevState.activeCanvasMessages,
          [canvasToEdit.id]: {
            ...prevState.activeCanvasMessages[canvasToEdit.id],
            content: updatedContent,
          },
        },
        canvasEditModalOpen: false,
        canvasToEdit: null,
      }));
    }
  };

  // Add this method to handle textarea resizing
  resizeTextarea = () => {
    // Find all chat input textareas and resize them
    const textareas = document.querySelectorAll(".chat-input");
    textareas.forEach((textarea) => {
      textarea.style.height = "48px";
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
    });
  };

  // Get theme color from facility settings with fallback
  getThemeColor = () => {
    const { appTheme = {} } = this.props;
    const themeColor = appTheme.themeColor
      ? appTheme.themeColor.replace("0x", "#")
      : "#6366f1"; // Fallback to default purple
    return themeColor;
  };
  
  // Handle agent selection
  handleSelectAgent = (agent) => {
    this.setState({ 
      selectedAgent: agent,
      showAgentSelector: false,
      messages: [], // Clear messages when switching agents
      sessionId: null, // Start new session
      showInitialPrompts: false // Hide initial prompts after selecting agent
    }, () => {
      // Load session for new agent
      this.loadSession();
      // Notify parent component about agent change
      if (this.props.onAgentChange) {
        this.props.onAgentChange(agent);
      }
    });
  };
  
  // Toggle agent selector
  toggleAgentSelector = () => {
    this.setState(prevState => ({
      showAgentSelector: !prevState.showAgentSelector
    }));
  };

  // Navigate to agents page
  goToAgentsPage = () => {
    if (this.props.history) {
      this.props.history.push('/app/h/agents');
    } else {
      window.location.href = '/app/h/agents';
    }
  };

  render() {
    const {
      messages,
      input,
      isLoading,
      isRecording,
      isTranscribing,
      isCitationPopupOpen,
      selectedCitationTitle,
      showInitialPrompts,
      autoPlayAudio,
      isLoadingHistory,
      availableAgents,
      selectedAgent,
      showAgentSelector,
    } = this.state;

    const { isRTL } = this.props;

    const showCentered =
      messages.length === 0 ||
      (messages.length === 1 && messages[0].role === "Assistant");
    
    // Get featured agents (first 3)
    const safeAvailableAgents = availableAgents || [];
    const featuredAgents = safeAvailableAgents.slice(0, 3);

    let micButtonIcon = <Mic className="icon" />;
    let micSrText = "Start voice recording";
    let micButtonColor = "secondary";
    let micButtonStyle = {};

    if (isRecording) {
      micButtonIcon = (
        <div
          className="icon"
          style={{
            width: "20px",
            height: "20px",
            backgroundColor: "white",
            borderRadius: "2px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      );
      micSrText = "Stop recording";
      micButtonColor = "danger";
      micButtonStyle = {
        backgroundColor: "#dc3545",
        borderColor: "#dc3545",
        color: "white",
      };
    } else if (isTranscribing) {
      micButtonIcon = <Loader2 className="icon animate-spin" />;
      micSrText = "Transcribing audio...";
      micButtonColor = "secondary";
    }

    const micButtonDisabled = isLoading || isTranscribing;
    const textInputAndSendDisabled = isLoading || isRecording || isTranscribing;

    // Get theme color for dynamic styling
    const themeColor = this.getThemeColor();

    // Add spacing constants for action buttons
    const HERO_ACTIONS_WIDTH = 100; // Space for large buttons
    const INPUT_ACTIONS_WIDTH = 80; // Space for small buttons

    return (
      <>
        <div
          className={cn(
            "chat-container rounded h-100",
            !showCentered && "expanded"
          )}
          style={{
            "--theme-color": themeColor,
            "--theme-color-light": themeColor + "20",
            "--theme-color-dark": themeColor + "dd",
          }}
        >
          {/* Header with autoplay toggle */}
          {(!showCentered || selectedAgent) && (
            <div className="chat-header">
              <div className="autoplay-toggle">
                <label
                  className="toggle-label"
                  title={
                    autoPlayAudio
                      ? I18nProvider.getIntlProvider().formatMessage(
                          Messages.disable_auto_play_voice
                        )
                      : I18nProvider.getIntlProvider().formatMessage(
                          Messages.enable_auto_play_voice
                        )
                  }
                >
                  <input
                    type="checkbox"
                    checked={autoPlayAudio}
                    onChange={this.toggleAutoPlay}
                    className="toggle-input"
                    aria-label={I18nProvider.getIntlProvider().formatMessage(
                      Messages.toggle_auto_play_voice
                    )}
                  />
                  <span className="toggle-switch"></span>
                  <span className="toggle-text">
                    <Text content={Messages.auto_play_responses} />
                  </span>
                </label>
              </div>
            </div>
          )}

          {isLoadingHistory ? (
            // Show spinner while loading history
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <Spinner
                  style={{
                    width: "3rem",
                    height: "3rem",
                    color: themeColor,
                  }}
                />
                <p className="mt-3">Loading conversation...</p>
              </div>
            </div>
          ) : showCentered ? (
            <div className="chat-hero">
              <div className="hero-header">
                <div className="autoplay-toggle">
                  <label
                    className="toggle-label"
                    title={
                      autoPlayAudio
                        ? I18nProvider.getIntlProvider().formatMessage(
                            Messages.disable_auto_play_voice
                          )
                        : I18nProvider.getIntlProvider().formatMessage(
                            Messages.enable_auto_play_voice
                          )
                    }
                  >
                    <input
                      type="checkbox"
                      checked={autoPlayAudio}
                      onChange={this.toggleAutoPlay}
                      className="toggle-input"
                      aria-label={I18nProvider.getIntlProvider().formatMessage(
                        Messages.toggle_auto_play_voice
                      )}
                    />
                    <span className="toggle-switch"></span>
                    <span className="toggle-text">
                      <Text content={Messages.auto_play_responses} />
                    </span>
                  </label>
                </div>
              </div>

              <h1 className="hero-text">{"How can I help you?"}</h1>
              
              {/* Featured Agents Section - only show in agent mode */}
              {!selectedAgent && featuredAgents.length > 0 && this.props.agentMode && (
                <div className="featured-agents-section">
                  <h2 className="section-title">
                    <Users size={18} className="mr-2" />
                    Chat with a Specialized Agent
                  </h2>
                  <div className="featured-agents-grid">
                    {featuredAgents.map(agent => (
                      <AgentCard
                        key={agent.id}
                        agent={agent}
                        onSelect={this.handleSelectAgent}
                        compact={true}
                        themeColor={themeColor}
                      />
                    ))}
                  </div>
                  {safeAvailableAgents.length > 3 && (
                    <button 
                      className="browse-agents-btn"
                      onClick={this.toggleAgentSelector}
                      style={{ color: themeColor }}
                    >
                      Browse all {safeAvailableAgents.length} agents
                    </button>
                  )}
                </div>
              )}
              
              {/* Link to Agents - only show in chat mode */}
              {!this.props.agentMode && (
                <div className="agents-link-section">
                  <h2 className="section-title">
                    <Bot size={18} className="mr-2" />
                    Chat with a Specialized Agent
                  </h2>
                  <button 
                    className="agents-link-card"
                    onClick={this.goToAgentsPage}
                    style={{ borderColor: themeColor }}
                  >
                    <div className="agents-link-content">
                      <Bot size={32} style={{ color: themeColor }} />
                      <div className="agents-link-text">
                        <h3 style={{ fontSize: "1rem" }}>Browse Agents</h3>
                        <p>Explore specialized agents for specific tasks</p>
                      </div>
                      <ArrowRight size={20} style={{ color: themeColor }} />
                    </div>
                  </button>
                </div>
              )}

              {/* Hero initial prompts - vertical list */}
              {showInitialPrompts && this.initialPrompts && this.initialPrompts.length > 0 && (
                <div className="initial-prompts-list">
                  <CardBody>
                    <div className="initial-prompts-wrapper">
                      {this.initialPrompts?.map((prompt, index) => (
                        <Button
                          key={index + 1}
                          onClick={() => this.setState({ input: prompt })}
                          className={`btn ${
                            input === prompt ? "selected" : ""
                          }`}
                          color="primary"
                          outline={true}
                          aria-pressed={input === prompt}
                          style={{
                            backgroundColor: "white",
                            borderColor: themeColor,
                            border: `2px solid ${themeColor}`,
                            color: "#333",
                            fontWeight: "500",
                            ...(input === prompt && {
                              backgroundColor: themeColor,
                              color: "white",
                              fontWeight: "600",
                            }),
                          }}
                        >
                          <span className="option-text">{prompt}</span>
                          {input === prompt && (
                            <span className="sr-only">(Selected)</span>
                          )}
                        </Button>
                      ))}
                    </div>
                  </CardBody>
                </div>
              )}

              <div className="hero-input-wrapper d-flex align-items-center">
                <textarea
                  value={input}
                  onChange={(e) => {
                    this.setState({ input: e.target.value });
                    const target = e.target;
                    target.style.height = "48px";
                    target.style.height =
                      Math.min(target.scrollHeight, 120) + "px";
                  }}
                  onKeyDown={this.handleKeyDown}
                  placeholder={Messages.ask_anything}
                  className="chat-input"
                  disabled={textInputAndSendDisabled}
                  aria-label="Chat input"
                  rows={1}
                  style={{
                    minHeight: "48px",
                    maxHeight: "120px",
                    resize: "none",
                    overflow: "hidden",
                    paddingRight: isRTL ? "12px" : `${HERO_ACTIONS_WIDTH}px`,
                    paddingLeft: isRTL ? `${HERO_ACTIONS_WIDTH}px` : "12px",
                    textAlign: isRTL ? "right" : "left",
                  }}
                />
                <div
                  className="hero-actions d-flex align-items-center"
                  style={{
                    position: "absolute",
                    right: isRTL ? "auto" : "12px",
                    left: isRTL ? "12px" : "auto",
                    gap: "8px",
                    zIndex: 2,
                  }}
                >
                  <Button
                    color={micButtonColor}
                    outline={!isRecording}
                    size="lg"
                    className="icon-btn"
                    onClick={this.handleToggleRecording}
                    disabled={micButtonDisabled}
                    title={micSrText}
                    aria-label={micSrText}
                    style={micButtonStyle}
                  >
                    {micButtonIcon}
                    <span className="sr-only">{micSrText}</span>
                  </Button>
                  <Button
                    size="lg"
                    onClick={this.handleSendMessage}
                    disabled={!input.trim() || textInputAndSendDisabled}
                    className={cn(
                      "icon-btn send-btn",
                      (!input.trim() || textInputAndSendDisabled) && "disabled"
                    )}
                    style={{
                      backgroundColor:
                        !input.trim() || textInputAndSendDisabled
                          ? "#e2e8f0"
                          : themeColor,
                      borderColor:
                        !input.trim() || textInputAndSendDisabled
                          ? "#e2e8f0"
                          : themeColor,
                      color:
                        !input.trim() || textInputAndSendDisabled
                          ? "#94a3b8"
                          : "#fff",
                    }}
                    title="Send message"
                    aria-label="Send message"
                  >
                    <Send className="icon" />
                    <span className="sr-only">Send message</span>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="chat-messages" ref={this.messagesBoxRef}>
              {messages.map((msg, index) => {
                // Check if this is the last historical message
                const isLastHistoricalMessage =
                  msg.isHistorical &&
                  (index === messages.length - 1 ||
                    !messages[index + 1].isHistorical);

                return (
                  <React.Fragment key={msg.id}>
                    {/* Render the actual message */}
                    {msg.role === "Canvas" ? (
                      <CanvasMessage
                        messageId={msg.id}
                        content={msg.content}
                        title={msg.canvasTitle}
                        loading={msg.canvasLoading}
                        isStreaming={!msg.textStreamComplete}
                        surveyData={msg.surveyData}
                        attributeData={msg.attributeData}
                        onEdit={() => this.handleOpenCanvasEditor(msg)}
                      />
                    ) : msg.role === "Assistant" ? (
                      <BotMessage
                        messageId={msg.id}
                        content={msg.content}
                        textStreamComplete={msg.textStreamComplete}
                        citations={msg.citations}
                        urls={msg.urls}
                        onCitationClick={this.handleOpenCitationPopup}
                        avatarSrc={selectedAgent?.avatarImage || this.avatarSrc}
                        audioData={msg.audioData}
                        audioMimeType={msg.audioMimeType}
                        autoPlayAudio={autoPlayAudio}
                        isHistorical={msg.isHistorical}
                        question={msg.asking_question ? msg.current_question_info : null}
                        onAnswerChange={msg.asking_question ? (value) => this.setState({ input: value }) : null}
                        agentName={selectedAgent?.name}
                      />
                    ) : (
                      <UserMessage
                        content={msg.content}
                        messageId={msg.id}
                        themeColor={themeColor}
                      />
                    )}

                    {/* Add separator after last historical message */}
                    {isLastHistoricalMessage && (
                      <div className="message-separator">
                        <div className="separator-line"></div>
                        <span className="separator-text">
                          Conversation resumed
                        </span>
                        <div className="separator-line"></div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {isLoading && (
                <div className="loading-indicator">
                  <em>{this.state.loadingText}</em>
                </div>
              )}

            </div>
          )}

          {/* Initial prompts carousel - horizontal scroll above input bar */}
          {!showCentered &&
            showInitialPrompts &&
            this.initialPrompts && this.initialPrompts.length > 0 && (
              <div className="initial-prompts-carousel">
                <div className="prompts-container">
                  <div className="prompts-scroll">
                    {this.initialPrompts?.map((prompt, index) => (
                      <MPSButton
                        key={`carousel-${index + 1}`}
                        onClick={() => this.setState({ input: prompt })}
                        className={`prompt-button ${
                          input === prompt ? "selected" : ""
                        }`}
                        color="secondary"
                        outline={true}
                        size="sm"
                        aria-pressed={input === prompt}
                        style={{
                          backgroundColor: "white",
                          borderColor: themeColor,
                          border: `2px solid ${themeColor}`,
                          color: "#333",
                          fontWeight: "500",
                          ...(input === prompt && {
                            backgroundColor: themeColor,
                            color: "white",
                            fontWeight: "600",
                          }),
                        }}
                      >
                        <span className="prompt-text">{prompt}</span>
                        {input === prompt && (
                          <span className="sr-only">(Selected)</span>
                        )}
                      </MPSButton>
                    ))}
                  </div>
                </div>
              </div>
            )}

          {/* Input bar - at bottom */}
          {!showCentered && (
            <div className="chat-input-bar">
              <div className="input-wrapper" style={{ position: "relative" }}>
                <textarea
                  value={input}
                  onChange={(e) => {
                    this.setState({ input: e.target.value });
                    const target = e.target;
                    target.style.height = "48px";
                    target.style.height =
                      Math.min(target.scrollHeight, 120) + "px";
                  }}
                  onKeyDown={this.handleKeyDown}
                  placeholder={Messages.ask_anything}
                  className="chat-input"
                  disabled={textInputAndSendDisabled}
                  aria-label="Chat input"
                  rows={1}
                  style={{
                    minHeight: "48px",
                    maxHeight: "120px",
                    resize: "none",
                    overflow: "hidden",
                    paddingRight: isRTL ? "12px" : `${INPUT_ACTIONS_WIDTH}px`,
                    paddingLeft: isRTL ? `${INPUT_ACTIONS_WIDTH}px` : "12px",
                    textAlign: isRTL ? "right" : "left",
                    width: "100%",
                  }}
                />
                <div
                  className="actions"
                  style={{
                    position: "absolute",
                    right: isRTL ? "auto" : "8px",
                    left: isRTL ? "8px" : "auto",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    zIndex: 2,
                  }}
                >
                  <Button
                    color={micButtonColor}
                    outline={!isRecording}
                    size="sm"
                    className="icon-btn"
                    onClick={this.handleToggleRecording}
                    disabled={micButtonDisabled}
                    title={micSrText}
                    aria-label={micSrText}
                    style={micButtonStyle}
                  >
                    {micButtonIcon}
                    <span className="sr-only">{micSrText}</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={this.handleSendMessage}
                    disabled={!input.trim() || textInputAndSendDisabled}
                    className={cn(
                      "icon-btn send-btn",
                      (!input.trim() || textInputAndSendDisabled) && "disabled"
                    )}
                    style={{
                      backgroundColor:
                        !input.trim() || textInputAndSendDisabled
                          ? "#e2e8f0"
                          : themeColor,
                      borderColor:
                        !input.trim() || textInputAndSendDisabled
                          ? "#e2e8f0"
                          : themeColor,
                      color:
                        !input.trim() || textInputAndSendDisabled
                          ? "#94a3b8"
                          : "#fff",
                    }}
                    title="Send message"
                    aria-label="Send message"
                  >
                    <Send className="icon" />
                    <span className="sr-only">Send message</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Agent Selector Modal */}
        <Modal
          isOpen={showAgentSelector}
          toggle={this.toggleAgentSelector}
          size="xl"
          className="agent-selector-modal"
        >
          <ModalBody className="p-0">
            <AgentSelector
              agents={safeAvailableAgents}
              onSelectAgent={this.handleSelectAgent}
              onClose={this.toggleAgentSelector}
              selectedAgent={selectedAgent}
              themeColor={themeColor}
              isRTL={isRTL}
            />
          </ModalBody>
        </Modal>
        
        {/* Citation popup */}
        <CitationInfoPopup
          isOpen={isCitationPopupOpen}
          toggle={this.toggleCitationPopup}
          citationTitle={selectedCitationTitle}
        />

        {/* Canvas edit modal */}
        {this.state.canvasToEdit && (
          <CanvasEditModal
            isOpen={this.state.canvasEditModalOpen}
            content={this.state.canvasToEdit.content}
            title={this.state.canvasToEdit.canvasTitle}
            surveyData={this.state.canvasToEdit.surveyData}
            attributeData={this.state.canvasToEdit.attributeData}
            onSave={this.handleSaveCanvas}
            onClose={this.handleCloseCanvasEditor}
            patientId={this.props.contextPatientId}
            isRTL={this.props.isRTL}
          />
        )}
      </>
    );
  }
}
const mapStateToProps = (state) => ({
  appTheme: state.user.data ? state.user.data.appTheme : {},
});

export default connect(mapStateToProps)(ChatContainer);
